import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Button, Form, Col } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import TemplateModel from '../components/templateModal';
import SearchTemplateModal from '../components/searchTemplateModal';
import VisitsDymanicTable from './visits-dynamic-table';
import { getMedicine, getAllVisits, getDose, getTiming, getFrequencies, getDurations } from '../../../../redux/visit';
import { updateTemplateItems, getTemplates, getTemplateById, createTemplate } from '../../../../redux/template';
import useDebounce from '../../../../utils/useDebounceHook';
import { optionsStyle, visitInitialValues } from '../composables';

const MedicineDropdown = ({ options, value, onChange, onSearch }) => (
	<Form.Group as={Col}>
		<CreatableSelect
			menuPortalTarget={document.body}
			styles={optionsStyle}
			isClearable
			options={options}
			value={value}
			onChange={onChange}
			onInputChange={(inputValue, { action }) => {
				if (action === 'input-change') {
					onSearch(inputValue);
				}
			}}
			isValidNewOption={(inputValue, _selectValue, selectOptions) =>
				!!inputValue.trim() &&
				!selectOptions.some(
					(opt) => opt.label.toLowerCase() === inputValue.trim().toLowerCase()
				)
			}
			filterOption={(option, rawInput) =>
				(!value || value.label.toLowerCase() !== option.label.toLowerCase()) &&
				option.label.toLowerCase().includes(rawInput.toLowerCase())
			}
		/>
	</Form.Group>
);

const RxComponent = ({ customTitle, isHideIcons = false, onClose = () => {}, isFromSettings = false, search = '' }) => {
	const { values, setFieldValue, errors, touched, setFieldError, setFieldTouched } = useFormikContext();
	const [errorMessage, setErrorMessage] = useState([]);
	const [model, setModel] = useState(false);
	const { medicines, doses, timings, frequencies, durations } = useSelector((state) => state.visits);
	const [searchTerms, setSearchTerms] = useState({
		medicine: '',
		dose: '',
		timing: '',
		frequency: '',
		duration: ''
	});
	const [medicineOptions, setMedicineOptions] = useState([]);
	const [doseOptions, setDoseOptions] = useState([]);
	const [timingOptions, setTimingOptions] = useState([]);
	const [frequencyOptions, setFrequencyOptions] = useState([]);
	const [durationOptions, setDurationOptions] = useState([]);
	const debouncedSearch = useDebounce(searchTerms, 500);
	const dispatch = useDispatch();
	const params = useParams();
	const [searchModel, setSearchModel] = useState(false);
	const { list: templates } = useSelector((state) => state.templates);

	useEffect(() => {
		if (isFromSettings) {
			setFieldValue('rx', [...visitInitialValues.rx]);
		}
	}, [isFromSettings]);

	const handleValueChange = (index, field, value, newValue) => {
		const updatedRx = values.rx.map((item) => ({ ...item }));
		updatedRx[index][field] = value;
		if (field === 'medicineId') {
			updatedRx[index]['medicineName'] = newValue;
		}
		if (field === 'doseId') {
			updatedRx[index]['doseName'] = newValue;
		}
		if (field === 'timingId') {
			updatedRx[index]['timingName'] = newValue;
		}
		if (field === 'frequencyId') {
			updatedRx[index]['frequencyName'] = newValue;
		}
		if (field === 'durationId') {
			updatedRx[index]['durationName'] = newValue;
		}
		setFieldValue('rx', updatedRx);
	};

	const handleNewMedicine = (index, medicineName) => {
		const isDuplicate = medicineOptions.some(
			opt => opt.label.toLowerCase() === medicineName.toLowerCase()
		) || values.rx.some(
			medicine => medicine.medicineName?.toLowerCase() === medicineName.toLowerCase()
		);
		if (isDuplicate) {
			toast.error('This medicine already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: medicineName, value: tempId };
		setMedicineOptions((prev) => [...prev, newOption]);
		handleValueChange(index, 'medicineId', tempId, medicineName);
	};

	const handleNewDose = (index, doseName) => {
		const isDuplicate = doseOptions.some(
			opt => opt.label.toLowerCase() === doseName.toLowerCase()
		) || values.rx.some(
			medicine => medicine.doseName?.toLowerCase() === doseName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This dose already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: doseName, value: tempId };
		setDoseOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'doseId', tempId, doseName);
	};

	const handleNewTiming = (index, timingName) => {
		const isDuplicate = timingOptions.some(
			opt => opt.label.toLowerCase() === timingName.toLowerCase()
		) || values.rx.some(
			medicine => medicine.timingName?.toLowerCase() === timingName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This timing already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: timingName, value: tempId };
		setTimingOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'timingId', tempId, timingName);
	};

	const handleNewFrequency = (index, frequencyName) => {

		const isDuplicate = frequencyOptions.some(
			opt => opt.label.toLowerCase() === frequencyName.toLowerCase()
		) || values.rx.some(
			medicine => medicine.frequencyName?.toLowerCase() === frequencyName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This frequency already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: frequencyName, value: tempId };
		setFrequencyOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'frequencyId', tempId, frequencyName);
	};

	const handleNewDuration = (index, durationName) => {

		const isDuplicate = durationOptions.some(
			opt => opt.label.toLowerCase() === durationName.toLowerCase()
		) || values.rx.some(
			medicine => medicine.durationName?.toLowerCase() === durationName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This duration already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: durationName, value: tempId };
		setDurationOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'durationId', tempId, durationName);
	};

	const addNewRow = () => {
		const newRow = {
			id: values.rx.length + 1,
			medicineId: '',
			dose: '',
			timing: '',
			frequency: '',
			duration: '',
			note: ''
		};
		setFieldValue('rx', [...values.rx, newRow]);
		setErrorMessage([]);
	};

	const handleDelete = (index) => {
		const updatedRx = values.rx.filter((_, i) => i !== index);
		setFieldValue('rx', updatedRx);
	};

	const handleSearchChange = (type) => (value) => {
		setSearchTerms((prev) => ({ ...prev, [type]: value }));
	};

	const mergeOptions = (setState) => (items) => {
		if (!items?.length) return;
		const newOptions = items.map(item => ({
			label: item.name,
			value: item.id
		}));
		setState(prevOptions => {
			const existingIds = new Set(prevOptions.map(opt => opt.value));
			const merged = [...prevOptions];
			newOptions.forEach(opt => {
				if (!existingIds.has(opt.value)) {
					merged.push(opt);
				}
			});
			return merged;
		});
	};

	const handleSelectTemplate = (option) => {
		dispatch(getTemplateById({ id: option.value })).unwrap().then(res => {
			setSearchModel(false);
			const fetchedPrescription = res.response.data.data.prescriptionItems;
			const newPrescription = fetchedPrescription.map((prescription, index) => ({
				id: index+1,
				medicineId: prescription.medicine_id,
				doseId: prescription.dose_id,
				timingId: prescription.timing_id,
				frequencyId: prescription.frequency_id,
				durationId: prescription.duration_id,
				note: prescription.notes || ''
			}));
			setFieldValue('rx', newPrescription);
			if (
				newPrescription.length > 0 &&
				typeof errors.rx === 'string'
			) {
				setFieldError('rx', undefined);
				setFieldTouched('rx', false);
			}
		});
	};

	const validateRx = () => {
		const errors = [];
		if (values.rx.length === 0) {
			errors.push('Please add at least one medicine');
		} else {
			values.rx.forEach((medicine, index) => {
				if (!medicine.medicineId) {
					errors.push(`Row ${index + 1}: Medicine is required`);
				}
				if (!medicine.doseId) {
					errors.push(`Row ${index + 1}: Dose is required`);
				}
				if (!medicine.timingId) {
					errors.push(`Row ${index + 1}: Timing is required`);
				}
				if (!medicine.frequencyId) {
					errors.push(`Row ${index + 1}: Frequency is required`);
				}
				if (!medicine.durationId) {
					errors.push(`Row ${index + 1}: Duration is required`);
				}
				if (!medicine.note) {
					errors.push(`Row ${index + 1}: Note is required`);
				}
			});
		}
		if (errors.length > 0) {
			setErrorMessage(errors);
			return false;
		} else {
			setErrorMessage([]);
			return true;
		}
	};

	const saveTemplate = async() => {
		if (validateRx()) {
			const existingTemplate = values.rx.find(medicine => medicine.existingTemplateId);
			const existingTemplateId = existingTemplate?.existingTemplateId;
			const payload = {
				templateId: existingTemplateId || null,
				prescriptions: values.rx.map(medicine => {
					let medicineData = {};
					if (typeof medicine.medicineId === 'string' && medicine.medicineId.startsWith('temp-')) {
						medicineData = { ...medicineData, medicineName: medicine.medicineName };
					} else if (typeof medicine.medicineId === 'number') {
						medicineData = { ...medicineData, medicineId: medicine.medicineId };
					}
					if (typeof medicine.doseId === 'string' && medicine.doseId.startsWith('temp-')) {
						medicineData = { ...medicineData, doseName: medicine.doseName };
					} else if (typeof medicine.doseId === 'number') {
						medicineData = { ...medicineData, doseId: medicine.doseId };
					}
					if (typeof medicine.timingId === 'string' && medicine.timingId.startsWith('temp-')) {
						medicineData = { ...medicineData, timingName: medicine.timingName };
					} else if (typeof medicine.timingId === 'number') {
						medicineData = { ...medicineData, timingId: medicine.timingId };
					}
					if (typeof medicine.frequencyId === 'string' && medicine.frequencyId.startsWith('temp-')) {
						medicineData = { ...medicineData, frequencyName: medicine.frequencyName };
					} else if (typeof medicine.frequencyId === 'number') {
						medicineData = { ...medicineData, frequencyId: medicine.frequencyId };
					}
					if (typeof medicine.durationId === 'string' && medicine.durationId.startsWith('temp-')) {
						medicineData = { ...medicineData, durationName: medicine.durationName };
					} else if (typeof medicine.durationId === 'number') {
						medicineData = { ...medicineData, durationId: medicine.durationId };
					}
					return {
						...medicineData,
						id: medicine.existingTemplateItemId || null,
						notes: medicine.note
					};
				})
			};
			if (existingTemplateId) {
				await dispatch(updateTemplateItems(payload)).unwrap().then(() => {
					dispatch(getTemplates({ type: 'prescription', search: search, per_page: 10, pId: Number(params.id) }));
					onClose();
				}).catch((err) => {
					toast.error(err.message || 'Something went wrong');
				});
			} else {
				setModel(true);
			}
		} else {
			return;
		}
	};

	const submitTemplate = async(name) => {
		const payload = {
			pId: Number(params.id),
			name: name,
			type: 'prescription',
			prescriptions: values.rx.map(medicine => {
				let medicineData = {};
				if (typeof medicine.medicineId === 'string' && medicine.medicineId.startsWith('temp-')) {
					medicineData = { ...medicineData, medicineName: medicine.medicineName };
				} else if (typeof medicine.medicineId === 'number') {
					medicineData = { ...medicineData, medicineId: medicine.medicineId };
				}
				if (typeof medicine.doseId === 'string' && medicine.doseId.startsWith('temp-')) {
					medicineData = { ...medicineData, doseName: medicine.doseName };
				} else if (typeof medicine.doseId === 'number') {
					medicineData = { ...medicineData, doseId: medicine.doseId };
				}
				if (typeof medicine.timingId === 'string' && medicine.timingId.startsWith('temp-')) {
					medicineData = { ...medicineData, timingName: medicine.timingName };
				} else if (typeof medicine.timingId === 'number') {
					medicineData = { ...medicineData, timingId: medicine.timingId };
				}
				if (typeof medicine.frequencyId === 'string' && medicine.frequencyId.startsWith('temp-')) {
					medicineData = { ...medicineData, frequencyName: medicine.frequencyName };
				} else if (typeof medicine.frequencyId === 'number') {
					medicineData = { ...medicineData, frequencyId: medicine.frequencyId };
				}
				if (typeof medicine.durationId === 'string' && medicine.durationId.startsWith('temp-')) {
					medicineData = { ...medicineData, durationName: medicine.durationName };
				} else if (typeof medicine.durationId === 'number') {
					medicineData = { ...medicineData, durationId: medicine.durationId };
				}
				return {
					...medicineData,
					notes: medicine.note
				};
			})
		};
		await dispatch(createTemplate(payload)).unwrap().then(() => {
			dispatch(getTemplates({ type: 'prescription', search: search, per_page: 10, pId: Number(params?.id || 0) }));
			setModel(false);
			onClose();
		}).catch((err) => {
			toast.error(err.message || 'Something went wrong');
		});
	};

	const openSearchTemplate = () => {
		setSearchModel(true);
		dispatch(getTemplates({ type: 'prescription', search: search, per_page: 10, pId: Number(params?.id || 0) }));
	};

	const handleLoadLastVisit = () => {
		dispatch(getAllVisits({ pId: Number(params?.id || 0), type: 'all', page: 1, per_page: 1 })).unwrap().then(res => {
			setSearchModel(false);
			const visits = res.response.data.data.data;
			if (visits.length ===0) {
				return;
			}
			const fetchedPrescription = visits[0].prescriptions;
			const newPrescription = fetchedPrescription && fetchedPrescription.length > 0 ? fetchedPrescription.map((prescription, index) => ({
				id: index + 1,
				medicineId: prescription.medicine_id,
				doseId: prescription.dose_id,
				timingId: prescription.timing_id,
				frequencyId: prescription.frequency_id,
				durationId: prescription.duration_id,
				note: prescription.notes || ''
			})) : visitInitialValues.rx;
			setFieldValue('rx', newPrescription);
			if (
				newPrescription.length > 0 &&
				typeof errors.rx === 'string'
			) {
				setFieldError('rx', undefined);
				setFieldTouched('rx', false);
			}
		});
	};

	const columns = [
		{
			name: '#',
			selector: (_, index) => index + 1,
			sortable: true,
			width: '50px'
		},
		{
			name: t('VISIT.MIDICINE'),
			cell: (row, index) => {
				const selectedIds = values.rx
					.filter((_, i) => i !== index)
					.map(c => c.medicineId);

				const filteredOptions = medicineOptions.filter(
					opt => !selectedIds.includes(opt.value)
				);
				return (
					<MedicineDropdown
						options={filteredOptions}
						value={filteredOptions.find(opt => opt.value === row.medicineId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewMedicine(index, option.label);
							} else {
								handleValueChange(index, 'medicineId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('medicine')}
					/>
				);
			},
			sortable: false,
			width: '300px',
		},
		{
			name: t('VISIT.DOSE'),
			cell: (row, index) => {
				return (
					<MedicineDropdown
						options={doseOptions}
						value={doseOptions.find(opt => opt.value === row.doseId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewDose(index, option.label);
							} else {
								handleValueChange(index, 'doseId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('dose')}
					/>
				);
			},
			sortable: false,
			width: '200px',
		},
		{
			name: t('ALLOPATHY.NAV.TIMING'),
			cell: (row, index) => {
				return (
					<MedicineDropdown
						options={timingOptions}
						value={timingOptions.find(opt => opt.value === row.timingId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewTiming(index, option.label);
							} else {
								handleValueChange(index, 'timingId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('timing')}
					/>
				);
			},
			sortable: false,
			width: '200px',
		},
		{
			name: t('VISIT.FREQUENCY'),
			cell: (row, index) => {
				return (
					<MedicineDropdown
						options={frequencyOptions}
						value={frequencyOptions.find(opt => opt.value === row.frequencyId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewFrequency(index, option.label);
							} else {
								handleValueChange(index, 'frequencyId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('frequency')}
					/>
				);
			},
			sortable: false,
			width: '200px',
		},
		{
			name: t('VISIT.DURATION'),
			cell: (row, index) => {
				return (
					<MedicineDropdown
						options={durationOptions}
						value={durationOptions.find(opt => opt.value === row.durationId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewDuration(index, option.label);
							} else {
								handleValueChange(index, 'durationId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('duration')}
					/>
				);
			},
			sortable: false,
			width: '200px',
		},
		{
			name: t('VISIT.NOTES_INSTRUCTIONS'),
			cell: (row, index) => (
				<Form.Control
					plaintext
					className="editable-cell p-1 w-100"
					value={row.note || ''}
					onChange={(e) => handleValueChange(index, 'note', e.target.value)}
				/>
			),
			sortable: false,
			width: '200px',
		},
		{
			name: t('CFORMS.COLUMNS.ACTIONS'),
			cell: (_row, index) => (
				<Button
					title={t('GENERAL.DELETE')}
					size="sm"
					variant="danger"
					onClick={() => handleDelete(index)}
				>
					<MdDelete size={18} />
				</Button>
			),
			width: '80px',
		},
	];

	useEffect(() => {
		mergeOptions(setMedicineOptions)(medicines);
	}, [medicines]);
	useEffect(() => {
		mergeOptions(setDoseOptions)(doses);
	}, [doses]);
	useEffect(() => {
		mergeOptions(setTimingOptions)(timings);
	}, [timings]);
	useEffect(() => {
		mergeOptions(setFrequencyOptions)(frequencies);
	}, [frequencies]);
	useEffect(() => {
		mergeOptions(setDurationOptions)(durations);
	}, [durations]);

	useEffect(() => {
		const { medicine, dose, timing, frequency, duration } = debouncedSearch;
		if (medicine) {
			dispatch(getMedicine({ search: debouncedSearch, status: 'active', per_page: 5 }));
		}
		if (dose) {
			dispatch(getDose({ search: debouncedSearch, status: 'active', per_page: 5 }));
		}
		if (timing) {
			dispatch(getTiming({ search: debouncedSearch, status: 'active', per_page: 5 }));
		}
		if (frequency) {
			dispatch(getFrequencies({ search: debouncedSearch, status: 'active', per_page: 5 }));
		}
		if (duration) {
			dispatch(getDurations({ search: debouncedSearch, status: 'active', per_page: 5 }));
		}
	}, [debouncedSearch]);


	return (
		<>
			<VisitsDymanicTable
				title={customTitle}
				columns={columns}
				data={values.rx || []}
				addRow={addNewRow}
				saveTemplate={saveTemplate}
				searchTemplate={openSearchTemplate}
				errorMessage={errorMessage}
				handleDelete={handleDelete}
				loadLastVisit={handleLoadLastVisit}
				isHideIcons={isHideIcons}
			/>
			<TemplateModel
				show={model}
				close={() => setModel(false)}
				submitTemplate={submitTemplate}
			/>
			<SearchTemplateModal
				show={searchModel}
				close={() => setSearchModel(false)}
				type="Prescription"
				options={templates}
				handleSelectTemplate={handleSelectTemplate}
				pId={Number(params.id)}
				isFromSettings={isFromSettings}
			/>
			{typeof errors.rx === 'string' && touched.rx && (
				<Form.Text className="text-danger mb-2 d-block">
					{errors.rx}
				</Form.Text>
			)}
		</>
	);
};

export default RxComponent;